import React from 'react';
import dynamic from 'next/dynamic';
import { ApplicationContextProvider } from '@directv-digital/dds.server.application-context';

const ErrorDisplay = dynamic(() => import("@directv-digital/dds.ui.error-display"));
import { CacheProvider/* , EmotionCache*/ } from '@emotion/react';
import createEmotionCache from '../src/createEmotionCache';
import { suppressConsoleMethods } from 'utils/suppressConsoleMethods';
import { StoryblokBridgeScript } from "app/components/head/StoryblokBridgeScript";

// Block console statements on client side in production ONLY
suppressConsoleMethods();

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

const App = (props): JSX.Element => {
  try {
    const { Component, pageProps, emotionCache = clientSideEmotionCache } = props || {};

    React.useEffect(() => {
      if (pageProps?.sbConfigs?.sbPreviewAllowed && pageProps?.sbConfigs?.useStoryblok) {
        import('@storyblok/react').then(({ storyblokInit, apiPlugin }) => {
          storyblokInit({
            ...pageProps?.sbConfigs?.storyblokInitParams,
            use: [apiPlugin]
          });
        });
      }

      // Remove the server-side injected CSS.
      const jssStyles = document.querySelector("#jss-server-side");
      if (jssStyles?.parentElement) {
        jssStyles.parentElement.removeChild(jssStyles);
      }
    }, []);

    const applicationContextValue = React.useMemo(() => ({
      apiPaths: (pageProps?.apiPaths || {}),
      isDCP: pageProps?.isDCP,
      breadcrumbHomeURL: pageProps?.breadcrumbHomeURL,
      sbConfigs: pageProps?.sbConfigs,
      globalConfigs: pageProps?.globalFeaturesConfig,
      // WAT-10279: Initial state is "no modal active." Previously this was
      // disableModalStack: true and relied on Revisitor's useEffect setting it
      // to false on the "won't show" path. With the orchestrator now filtering
      // Revisitor out of the tree when no cart is active, that useEffect never
      // runs and Exit stays blocked. Default-to-clear matches the gate's intent.
      revisitorModalState: {
        state: "NA",
        disableModalStack: false
      },
      globalRsnFee: pageProps?.globalRsnFee || "",
      globalZipCode: "",
      apiKeys: pageProps?.apiKeys || pageProps?.secrets || {}
    }), [pageProps]);

    let enableLivePreview = false;
    if (pageProps?.sbConfigs?.sbPreviewAllowed && pageProps?.sbConfigs?.useStoryblok) {
      const reqQueryParams = pageProps?.reqQueryParams || {};
      enableLivePreview = Boolean(reqQueryParams._storyblok_release);
    }

    return (
      <>
      <StoryblokBridgeScript enabled={enableLivePreview} />
      <CacheProvider value={emotionCache}>
        <ApplicationContextProvider value={applicationContextValue}>
          <Component {...pageProps} />
          <div id="tcEmbeddedC2CB"></div>
        </ApplicationContextProvider>
      </CacheProvider>
      </>
    );
  } catch (error) {
    console.error("##", error);
    return (
      <ErrorDisplay error={error} />
    );
  }
};

export default App;
